/**
 * Zod schema + stage metadata for the `/job-published` sourcing progress
 * tracker (GitHub issue #498 — "E2E: Sourcing & outreach — pipeline
 * tracking, email management, replies").
 *
 * Mirrors the real `sourcing_jobs` row shape already returned by
 * `functions/api/sourcing_jobs.ts`'s `SourcingJobQueryResponse`
 * (`candidatesSourced`, `funnelDecomposition`, `status`) but adds the
 * `screenedCount`/`queuedCount`/`stage` fields the progress UI needs,
 * derived from `funnelDecomposition` (`scored`, `queued`) so this page can
 * poll a single endpoint instead of re-deriving stage from raw counts on
 * the client.
 *
 * UPDATE (GitHub issue #564, "[Metrics] Customer-facing 'Contacted'
 * overstates — counts merely-QUEUED candidates"): `GET
 * /api/jobs/:jobId/sourcing-progress` is now implemented for real —
 * `apps/workers/src/api/jobs/sourcingProgress.ts`, backed by the
 * canonical derivation in `apps/workers/src/lib/sourcingProgressMetrics.ts`
 * — closing the TODO this comment used to describe. `candidatesContacted`
 * below is that issue's fix: a count derived ONLY from candidates with a
 * CONFIRMED outreach send (`matches.funnel_status` at `OUTREACH_SENT` or
 * later), so it can never overstate the way the old "Contacted" lens did
 * (counting `OUTREACH_QUEUED` candidates who were never actually emailed).
 * `candidatesQueued` is kept separate and honestly labeled "Queued for
 * outreach" in the UI (`JobPublished.tsx`) — never "Contacted".
 */

import { z } from 'zod';

export const sourcingStageSchema = z.enum([
  'SOURCING',
  'SCREENING',
  'OUTREACH_QUEUING',
  'COMPLETE',
]);
export type SourcingStage = z.infer<typeof sourcingStageSchema>;

export const sourcingProgressResponseSchema = z.object({
  jobId: z.string(),
  jobTitle: z.string().nullable().optional(),
  stage: sourcingStageSchema,
  candidatesSourced: z.number().int().nonnegative(),
  candidatesScreened: z.number().int().nonnegative(),
  candidatesQueued: z.number().int().nonnegative(),
  /**
   * Count of candidates with a CONFIRMED outreach send (Issue #564's fix —
   * see this file's module doc-comment). `.optional().default(0)` so
   * older mocked responses/fixtures that predate this field (e.g. any
   * E2E fixture not yet updated) still parse rather than throwing —
   * defaulting to 0 is itself honest (never overstates "Contacted").
   */
  candidatesContacted: z.number().int().nonnegative().optional().default(0),
  /**
   * Count of candidates at the `SHORTLISTED` status (GitHub issue "P0:
   * Customer churn — Tom ... dashboard stale, pipeline broken"). Its own
   * honest number, not folded into `candidatesContacted` — `.default(0)`
   * for the same backward-compatibility reason as `candidatesContacted`.
   */
  candidatesShortlisted: z.number().int().nonnegative().optional().default(0),
  /**
   * The true "last activity" timestamp for this job (see
   * `apps/workers/src/api/jobs/sourcingProgress.ts`'s module doc-comment)
   * — never a stale cached value. `null` if genuinely unknown.
   */
  lastActivityAt: z.string().nullable().optional().default(null),
  /**
   * `true` when `lastActivityAt` is older than the stale threshold and
   * the run hasn't completed — surfaces a crashed/frozen pipeline
   * honestly instead of silently looking like ongoing progress.
   */
  stalled: z.boolean().optional().default(false),
  isComplete: z.boolean(),
  /**
   * GitHub issue #195/#522 ("[Email/UX] 'Sourcing complete — 0 ready for
   * outreach' must explain WHY"). Non-null only when this run's
   * `candidatesQueued` is 0 — see
   * `apps/workers/src/lib/zeroQueuedReasonBreakdown.ts` for the
   * derivation this mirrors, and `JobPublished.tsx`/`Dashboard.tsx` for
   * where it's rendered. `.optional()` + `.nullable().default(null)` so
   * older mocked responses (predating this field) still parse, and the
   * UI's own `candidatesQueued === 0` check is the real gate — a `null`
   * value here (missing/unavailable data) still renders a visible
   * "Reason breakdown unavailable" placeholder rather than a bare
   * unexplained zero (this issue's negative AC).
   */
  zeroQueuedReasonBreakdown: z
    .object({
      show: z.boolean(),
      available: z.boolean(),
      buckets: z.array(
        z.object({
          key: z.enum(['noEmail', 'alreadyContacted', 'belowThreshold']),
          label: z.string(),
          count: z.number().int().nonnegative(),
        })
      ),
      needsEmailEnrichmentCount: z.number().int().nonnegative().nullable(),
      breakdownText: z.string(),
    })
    .nullable()
    .optional()
    .default(null),
});
export type SourcingProgressResponse = z.infer<typeof sourcingProgressResponseSchema>;
export type ZeroQueuedReasonBreakdownResponse = NonNullable<
  SourcingProgressResponse['zeroQueuedReasonBreakdown']
>;

/** The 4 stages the progress tracker renders, in order. `metricKey` says
 * which `SourcingProgressResponse` count that stage's card should display
 * once it's been reached (so counts "update in real-time" as later polls
 * come back with higher stages). */
export const SOURCING_STAGES: Array<{
  key: SourcingStage;
  label: string;
  description: string;
  metricKey:
    | keyof Pick<
        SourcingProgressResponse,
        'candidatesSourced' | 'candidatesScreened' | 'candidatesQueued'
      >
    | null;
  metricLabel: string | null;
}> = [
  {
    key: 'SOURCING',
    label: 'Sourcing Candidates',
    description: 'Searching the web for people who match your role',
    metricKey: 'candidatesSourced',
    metricLabel: 'Candidates found',
  },
  {
    key: 'SCREENING',
    label: 'Screening Candidates',
    description: 'Evaluating each candidate against your criteria',
    metricKey: 'candidatesScreened',
    metricLabel: 'Candidates screened',
  },
  {
    key: 'OUTREACH_QUEUING',
    label: 'Preparing Outreach',
    description: 'Drafting personalized emails for your top matches',
    metricKey: 'candidatesQueued',
    metricLabel: 'Queued for outreach',
  },
  {
    key: 'COMPLETE',
    label: 'Complete',
    description: 'Your pipeline is ready',
    metricKey: null,
    metricLabel: null,
  },
];

/** Index of a stage in `SOURCING_STAGES`, used to decide whether a given
 * stage card is "done" / "active" / "upcoming" relative to the latest
 * poll's `stage`. */
export function stageIndex(stage: SourcingStage): number {
  return SOURCING_STAGES.findIndex((s) => s.key === stage);
}
